import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import LanguageSwitcher from '../../components/LanguageSwitcher';

/**
 * Unified login page for all roles
 * After successful login, redirects to /dashboard
 */
function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading } = useAuth();
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      // Redirect to where they came from, or dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, location]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(email, password);
      // Redirect handled by useEffect above
    } catch (err) {
      setError(err.message || t('auth.invalidCredentials'));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div className="flex justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)', display: 'flex', alignItems: 'center' }}>
      <div className="container">
        <div className="card" style={{ maxWidth: '400px', margin: '0 auto' }}>
          <div className="flex justify-between items-center mb-4">
            <h1 style={{ fontSize: '24px', margin: 0 }}>
              {t('common.login')}
            </h1>
            <LanguageSwitcher />
          </div>
          <p className="text-secondary text-center mb-4">{t('auth.loginSubtitle')}</p>

          {error && (
            <div style={{
              background: '#fef2f2',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">{t('common.email')}</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label className="label">{t('common.password')}</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={submitting}
            >
              {submitting ? t('common.loading') : t('common.login')}
            </button>
          </form>

          <div className="text-center mt-4">
            <p className="text-secondary text-sm mb-2">{t('auth.noAccount')}</p>
            <div className="flex justify-center gap-3">
              <Link to="/register/owner" className="text-sm">
                {t('auth.registerAsOwner')}
              </Link>
              <span className="text-secondary">|</span>
              <Link to="/register/vendor" className="text-sm">
                {t('auth.registerAsVendor')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
